import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { testimonials } from "@/mocks/movies";

export default function AboutPage() {
  const navigate = useNavigate();
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as const });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormStatus("success");
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== "website_alt" || !value || String(value).trim() === "") {
          urlEncoded.append(key, String(value));
        }
      });

      const response = await fetch("https://readdy.ai/api/form/d9n4em0bdkpk99j7gg70", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, string> | null)?.meta?.message
          || (parsed as Record<string, string> | null)?.message
          || "提交失败，请稍后重试";
        setFormError(String(serverMsg));
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cinema%20interior%20with%20elegant%20golden%20lighting%2C%20red%20velvet%20curtains%2C%20empty%20movie%20theater%20seats%20with%20soft%20ambient%20light%2C%20sophisticated%20and%20inviting%20atmosphere%2C%20wide%20composition&width=1600&height=700&seq=about-hero-cinema&orientation=landscape"
            alt="关于青柠影院"
            title="青柠影院 - 关于我们"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background-50"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-[1440px] mx-auto">
              <h1 className="text-2xl md:text-4xl font-bold text-white">关于我们</h1>
              <p className="text-white/70 text-sm md:text-base mt-2">
                了解青柠影院的故事与使命
              </p>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="hover:text-primary-500 transition-colors"
            >
              首页
            </a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">关于我们</span>
          </nav>
        </div>

        {/* Brand Introduction */}
        <section id="brand" className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="text-xs font-semibold text-primary-500 uppercase tracking-wider">
                品牌介绍
              </span>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mt-2">
                打造极致的在线观影体验
              </h2>
              <div className="w-16 h-1 bg-primary-500 mt-4 rounded-full"></div>
              <div className="mt-6 space-y-4 text-sm text-foreground-700 leading-relaxed">
                <p>
                  青柠影院成立于2024年，是一个专注于提供<strong className="text-foreground-950">最新电影在线观看</strong>服务的平台。
                  我们致力于为广大电影爱好者打造一个<strong className="text-foreground-950">高清电影全集免费看</strong>的观影天地，
                  让每个人都能轻松享受电影的乐趣。
                </p>
                <p>
                  我们的团队由一群热爱电影的年轻人组成，我们深知在快节奏的生活中，找到一个稳定、流畅、
                  免费的在线电影平台是多么重要。因此，我们打造了青柠影院——一个电影频道覆盖动作片、科幻片、
                  喜剧片、爱情片、恐怖片、纪录片、动画片、剧情片等八大类型的综合观影平台。
                </p>
                <p>
                  我们坚持<strong className="text-foreground-950">每日更新片库</strong>，
                  确保用户能第一时间追到<strong className="text-foreground-950">最新大片</strong>。
                  无论你是在<strong className="text-foreground-950">wifi下流畅观看</strong>，
                  还是使用移动数据，青柠影院都能为你提供不缓冲、不卡顿的观看体验。
                </p>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center p-4 rounded-xl bg-background-50 border border-background-200">
                  <div className="text-xl font-bold text-primary-500">10000+</div>
                  <div className="text-xs text-foreground-500 mt-1">高清片源</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-background-50 border border-background-200">
                  <div className="text-xl font-bold text-primary-500">8</div>
                  <div className="text-xs text-foreground-500 mt-1">电影分类</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-background-50 border border-background-200">
                  <div className="text-xl font-bold text-primary-500">24h</div>
                  <div className="text-xs text-foreground-500 mt-1">每日更新</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20home%20cinema%20setup%20with%20large%20screen%20displaying%20colorful%20movie%20scene%2C%20cozy%20living%20room%20ambiance%2C%20warm%20lighting%2C%20comfortable%20sofa%2C%20cinematic%20experience%20at%20home%2C%20elegant%20interior&width=800&height=600&seq=about-brand-cinema&orientation=landscape"
                alt="青柠影院品牌介绍"
                title="青柠影院 - 在线观影体验"
                className="w-full rounded-2xl"
              />
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="text-xs font-semibold text-primary-500 uppercase tracking-wider">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mt-2">
                听听用户怎么说
              </h2>
              <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-[600px] mx-auto">
                来自真实用户的评价，是我们不断进步的动力
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-6 rounded-xl bg-background-50 border border-background-200"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < t.rating
                            ? "ri-star-fill text-amber-500"
                            : "ri-star-line text-foreground-300"
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-semibold">
                        {t.name.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-foreground-800">{t.name}</span>
                    </div>
                    <span className="text-xs text-foreground-400">{t.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="text-xs font-semibold text-primary-500 uppercase tracking-wider">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mt-2">
                期待你的来信
              </h2>
              <p className="mt-3 text-foreground-600 text-sm md:text-base">
                有任何问题或建议？欢迎与我们联系
              </p>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="p-5 rounded-xl bg-background-50 border border-background-200 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950">电子邮箱</h4>
                <p className="text-xs text-foreground-500 mt-1">contact@qingningcinema.com</p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950">微信公众号</h4>
                <p className="text-xs text-foreground-500 mt-1">青柠影院官方</p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950">响应时间</h4>
                <p className="text-xs text-foreground-500 mt-1">24小时内回复</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200">
              <h3 className="text-lg font-semibold text-foreground-950 mb-6">发送消息</h3>
              {formStatus === "success" ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-green-100">
                    <i className="ri-check-line text-2xl text-green-600"></i>
                  </div>
                  <h4 className="mt-4 text-lg font-semibold text-foreground-950">发送成功！</h4>
                  <p className="mt-2 text-sm text-foreground-500">
                    感谢你的留言，我们会尽快回复
                  </p>
                  <button
                    onClick={() => setFormStatus("idle")}
                    className="mt-4 px-5 py-2 bg-background-200 hover:bg-background-300 text-foreground-700 text-sm font-medium rounded-full transition-colors cursor-pointer"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form
                  ref={formRef}
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {/* Spam honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入你的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入你的消息（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  {formStatus === "error" && formError && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-white font-semibold rounded-full transition-all duration-200 text-sm cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === "submitting" ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        发送中...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-send-plane-line"></i>
                        发送消息
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于青柠影院",
            description:
              "青柠影院 - 最新电影在线观看平台，提供高清电影全集免费看。了解青柠影院的品牌故事、用户口碑和联系方式。",
            isPartOf: {
              "@type": "WebSite",
              name: "青柠影院",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "关于我们",
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "青柠影院需要收费吗？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "青柠影院是一个免费的电影在线观看平台，所有电影均可免费观看，无需付费。",
                },
              },
              {
                "@type": "Question",
                name: "青柠影院的电影画质如何？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "青柠影院支持1080P和4K超高清画质，为您提供影院级观影体验。在wifi环境下可流畅观看不缓冲。",
                },
              },
              {
                "@type": "Question",
                name: "青柠影院有哪些电影分类？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "青柠影院涵盖了动作片、科幻片、喜剧片、爱情片、恐怖片、纪录片、动画片、剧情片等八大电影分类，满足不同观众的观影需求。",
                },
              },
              {
                "@type": "Question",
                name: "青柠影院的片库多久更新一次？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "青柠影院的电影频道每日更新，确保用户能第一时间追到最新大片，追新片快人一步。",
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}